var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, FieldWrapper, FlexBox, FormField, FormGrid, InputText, Page, Text, } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ratingApi } from 'apis/rating';
import { languages } from 'app/utils/supportedLanguages';
import { AppLink } from 'components/appLink';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
export const SurveyCustomizationTextToSpeechView = () => {
    const { settingsId, languageCode } = useAppPathParams('/settings/satisfaction/phone/survey/:settingsId/:languageCode');
    const language = useMemo(() => languages.filter((l) => l.code == languageCode).shift(), [languageCode]);
    const [textToSpeechCustomization, setTextToSpeechCustomization] = useState();
    const [introductoryMessage, setIntroductoryMessage] = useState();
    const [questionMessage, setQuestion] = useState();
    const [endingMessage, setEndingMessage] = useState();
    const navigate = useAppNavigate();
    const handleNavigateBackToSurvey = useCallback(() => {
        navigate({
            path: `/settings/satisfaction/phone/survey/:settingsId`,
            params: { path: { settingsId } },
        });
    }, [navigate, settingsId]);
    const [updateRatingSettings] = ratingApi.useLazyUpdateRatingSettingsQuery();
    const [createRatingSettings] = ratingApi.useLazyCreateRatingSettingsQuery();
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const [readRatingSettings, { data: readRatingSettingsResponse, isSuccess: readRatingSettingsIsSuccess, },] = ratingApi.useLazyReadRatingSettingsQuery();
    const [getSurveyRatingSettings, { data: surveyRatingSettings, isSuccess: surveySettingIsSuccess },] = ratingApi.useLazyGetRatingSettingsQuery();
    const isModified = useMemo(() => {
        var _a, _b;
        if (textToSpeechCustomization) {
            return (textToSpeechCustomization.introductory_message != introductoryMessage ||
                ((_b = (_a = textToSpeechCustomization.rating_questions) === null || _a === void 0 ? void 0 : _a.filter((question) => question === questionMessage)) !== null && _b !== void 0 ? _b : []).length === 0 ||
                textToSpeechCustomization.ending_message != endingMessage);
        }
        const modified = introductoryMessage !== undefined &&
            introductoryMessage !== '' &&
            questionMessage !== undefined &&
            questionMessage !== '' &&
            endingMessage !== undefined &&
            endingMessage !== '';
        return modified;
    }, [
        textToSpeechCustomization,
        introductoryMessage,
        questionMessage,
        endingMessage,
    ]);
    useEffect(() => {
        // Compares the original content with the current one using 'isModified' and updates enabled status of the save button accordingly.
        if (readRatingSettingsIsSuccess) {
            setIsSaveEnabled(isModified);
        }
    }, [readRatingSettingsIsSuccess, isModified]);
    useEffect(() => {
        // If no language is defined we don't allow to continue further with customizting the language settings
        if (!language) {
            handleNavigateBackToSurvey();
        }
    }, [handleNavigateBackToSurvey, language]);
    const handleCreateOrUpdateLanguageSettings = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const textToSpeechCustomization = {
            introductory_message: introductoryMessage !== null && introductoryMessage !== void 0 ? introductoryMessage : '',
            rating_questions: [questionMessage !== null && questionMessage !== void 0 ? questionMessage : ''],
            ending_message: endingMessage !== null && endingMessage !== void 0 ? endingMessage : '',
        };
        // Checks the fetched language settings, if they already exited it performs an update, if it did not it creates one.
        if (readRatingSettingsResponse === null || readRatingSettingsResponse === void 0 ? void 0 : readRatingSettingsResponse.data.rating_settings.length) {
            const updateReq = {
                id: readRatingSettingsResponse === null || readRatingSettingsResponse === void 0 ? void 0 : readRatingSettingsResponse.data.rating_settings[0].id,
                customization: textToSpeechCustomization,
            };
            updateRatingSettings(updateReq).then(() => setIsSaveEnabled(false));
        }
        else {
            yield createRatingSettings({
                language: languageCode,
                level: 'language',
                parent_id: settingsId,
                survey_type: 'text_to_speech',
                customization: textToSpeechCustomization,
            });
            setIsSaveEnabled(false);
            // After creating, we fetch the settings again, which means that next time the language
            // settings object will be present so it will perform an update instead of create
            fetchLanguageRatingSettings();
        }
    }), [
        introductoryMessage,
        questionMessage,
        endingMessage,
        updateRatingSettings,
        createRatingSettings,
        surveyRatingSettings,
    ]);
    /**
     * For some reason, casting CSATCustomization to
     * TextToSpeechRatingCustomization fails and returns an undefined object. This
     * is a workaround to convert the content back to Json and parse it back.
     */
    function withTextToSpeech(customization, handleTextToSpeech) {
        try {
            const textToSpeech = customization;
            return handleTextToSpeech(textToSpeech);
        }
        catch (_a) {
            return undefined;
        }
    }
    useEffect(() => {
        getSurveyRatingSettings({ ratingSettingsId: settingsId });
    }, [settingsId]);
    const fetchLanguageRatingSettings = useCallback(() => {
        var _a;
        readRatingSettings({
            language: languageCode,
            parentId: settingsId,
            level: 'language',
            surveyType: (_a = surveyRatingSettings === null || surveyRatingSettings === void 0 ? void 0 : surveyRatingSettings.data.survey_type) !== null && _a !== void 0 ? _a : 'text_to_speech',
        });
    }, [readRatingSettings, surveySettingIsSuccess]);
    useEffect(() => {
        if (surveyRatingSettings === null || surveyRatingSettings === void 0 ? void 0 : surveyRatingSettings.data) {
            fetchLanguageRatingSettings();
        }
    }, [surveyRatingSettings]);
    useEffect(() => {
        var _a;
        if (readRatingSettingsIsSuccess &&
            readRatingSettingsResponse &&
            readRatingSettingsResponse.data.rating_settings.length > 0) {
            const customization = (_a = readRatingSettingsResponse.data.rating_settings[0].customization) !== null && _a !== void 0 ? _a : undefined;
            withTextToSpeech(customization, (textToSpeech) => {
                var _a;
                setTextToSpeechCustomization(textToSpeech);
                setIntroductoryMessage(textToSpeech === null || textToSpeech === void 0 ? void 0 : textToSpeech.introductory_message);
                setEndingMessage(textToSpeech === null || textToSpeech === void 0 ? void 0 : textToSpeech.ending_message);
                if ((_a = textToSpeech === null || textToSpeech === void 0 ? void 0 : textToSpeech.rating_questions) === null || _a === void 0 ? void 0 : _a.length) {
                    setQuestion(textToSpeech === null || textToSpeech === void 0 ? void 0 : textToSpeech.rating_questions[0]);
                }
            });
        }
    }, [
        setIntroductoryMessage,
        setEndingMessage,
        setQuestion,
        readRatingSettingsResponse,
        readRatingSettingsIsSuccess,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(Page.Section, Object.assign({ titleMeta: _jsx(AppLink, Object.assign({ path: `/settings/satisfaction/phone/survey/:settingsId`, params: { path: { settingsId } } }, { children: "\u2190 Back to language options" })), controls: _jsx(Button, Object.assign({ variant: "primary", disabled: !isSaveEnabled, onClick: () => handleCreateOrUpdateLanguageSettings() }, { children: "Save" })) }, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "row", gap: "500" }, { children: [_jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "100" }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "1", subdued: true }, { children: "CSAT survey name" })), surveySettingIsSuccess && (surveyRatingSettings === null || surveyRatingSettings === void 0 ? void 0 : surveyRatingSettings.data) && (_jsx(Text.Paragraph, Object.assign({ variant: "1" }, { children: surveyRatingSettings === null || surveyRatingSettings === void 0 ? void 0 : surveyRatingSettings.data.name })))] })), _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "100" }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "1", subdued: true }, { children: "Language" })), _jsx(Text.Paragraph, Object.assign({ variant: "1" }, { children: language === null || language === void 0 ? void 0 : language.label }))] }))] })) })), _jsx(Page.Section, { children: _jsx(Text.Paragraph, Object.assign({ variant: "2" }, { children: "All defined messages will be turned into audio and played in order at the end of the phone call. Remember to ask your customer to leave a rating from 1 to 5, being 1 the lowest and 5 the highest." })) }), _jsx(Page.Section, { children: _jsxs(FormGrid, { children: [_jsx(FormField, { children: _jsx(FieldWrapper, Object.assign({ label: "Introductory message", helpIcon: {
                                    text: 'Plays at the end of the conversation, typically used to briefly introduce to the survey.',
                                    dark: true,
                                } }, { children: _jsx(InputText, { name: "introductory message input", placeholder: "Write your message", value: introductoryMessage, disabled: !readRatingSettingsIsSuccess, onChange: (e) => {
                                        setIntroductoryMessage(e.target.value);
                                    }, type: "text" }) })) }), _jsx(FormField, { children: _jsx(FieldWrapper, Object.assign({ label: "Question" }, { children: _jsx(InputText, { name: "question input", placeholder: "Write your question", value: questionMessage, disabled: !readRatingSettingsIsSuccess, onChange: (e) => {
                                        setQuestion(e.target.value);
                                    }, type: "text" }) })) }), _jsx(FormField, { children: _jsx(FieldWrapper, Object.assign({ label: "Ending message", helpIcon: {
                                    text: 'Plays after the end user responded, typically used to thank them.',
                                    dark: true,
                                } }, { children: _jsx(InputText, { name: "ending message input", placeholder: "Write your message", value: endingMessage, disabled: !readRatingSettingsIsSuccess, onChange: (e) => {
                                        setEndingMessage(e.target.value);
                                    }, type: "text" }) })) })] }) })] }));
};
